const DEFAULT_MONTH_COUNT = 60
const DEFAULT_MAX_AGE = 120

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1)

const addMonths = (date, amount) => (
  new Date(date.getFullYear(), date.getMonth() + amount, 1)
)

const isSameCalendarDate = (date, year, monthIndex, day) => (
  date.getFullYear() === year
  && date.getMonth() === monthIndex
  && date.getDate() === day
)

export const parseInquiryAgeDate = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime())
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1])
      const monthIndex = Number(dateOnlyMatch[2]) - 1
      const day = Number(dateOnlyMatch[3])
      const parsedDateOnly = new Date(year, monthIndex, day)
      return isSameCalendarDate(parsedDateOnly, year, monthIndex, day)
        ? parsedDateOnly
        : null
    }
  }

  if (value == null || value === '') return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const calculateAgeOnDate = (dobValue, inquiryDateValue) => {
  const dob = parseInquiryAgeDate(dobValue)
  const inquiryDate = parseInquiryAgeDate(inquiryDateValue)
  if (!dob || !inquiryDate) return null

  let age = inquiryDate.getFullYear() - dob.getFullYear()
  const birthdayHasOccurred =
    inquiryDate.getMonth() > dob.getMonth()
    || (
      inquiryDate.getMonth() === dob.getMonth()
      && inquiryDate.getDate() >= dob.getDate()
    )

  if (!birthdayHasOccurred) age -= 1
  return age
}

const formatMonthLabel = (date, includeYear = true) => {
  const month = date.toLocaleString('en-US', { month: 'short' })
  return includeYear ? `${month} ${date.getFullYear()}` : month
}

export const buildMonthlyInquiryAgeSeries = (
  families,
  {
    now = new Date(),
    monthCount = DEFAULT_MONTH_COUNT,
    maxAge = DEFAULT_MAX_AGE,
  } = {}
) => {
  const safeNow = parseInquiryAgeDate(now) || new Date()
  const safeMonthCount = Math.max(1, Math.floor(Number(monthCount) || DEFAULT_MONTH_COUNT))
  const lastMonth = startOfMonth(safeNow)
  const firstMonth = addMonths(lastMonth, -(safeMonthCount - 1))
  const endExclusive = addMonths(lastMonth, 1)
  const monthBuckets = new Map()

  for (let index = 0; index < safeMonthCount; index += 1) {
    const date = addMonths(firstMonth, index)
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    monthBuckets.set(key, {
      key,
      date,
      label: formatMonthLabel(date),
      shortLabel: formatMonthLabel(date, false),
      totalAge: 0,
      sampleSize: 0,
      adultCount: 0,
    })
  }

  let eligibleStudentCount = 0
  let missingDobCount = 0
  let excludedOutlierCount = 0

  ;(Array.isArray(families) ? families : []).forEach((family) => {
    const inquiryDate = parseInquiryAgeDate(family?.onboardDate)
    if (!inquiryDate || inquiryDate < firstMonth || inquiryDate >= endExclusive) return

    const students = Array.isArray(family?.students) ? family.students : []
    const key = `${inquiryDate.getFullYear()}-${String(inquiryDate.getMonth() + 1).padStart(2, '0')}`
    const bucket = monthBuckets.get(key)
    if (!bucket) return

    students.forEach((student) => {
      eligibleStudentCount += 1
      const age = calculateAgeOnDate(student?.dob, inquiryDate)
      if (!Number.isFinite(age)) {
        missingDobCount += 1
        return
      }
      if (age < 0 || age > maxAge) {
        excludedOutlierCount += 1
        return
      }

      bucket.totalAge += age
      bucket.sampleSize += 1
      if (age >= 18) bucket.adultCount += 1
    })
  })

  const months = Array.from(monthBuckets.values()).map((bucket) => ({
    key: bucket.key,
    date: bucket.date,
    label: bucket.label,
    shortLabel: bucket.shortLabel,
    averageAge: bucket.sampleSize
      ? Math.round((bucket.totalAge / bucket.sampleSize) * 10) / 10
      : null,
    sampleSize: bucket.sampleSize,
    adultCount: bucket.adultCount,
    adultPercentage: bucket.sampleSize
      ? Math.round((bucket.adultCount / bucket.sampleSize) * 1000) / 10
      : null,
  }))

  const includedStudentCount = months.reduce((sum, month) => sum + month.sampleSize, 0)
  const includedAgeTotal = months.reduce((sum, month) => {
    if (!month.sampleSize || month.averageAge == null) return sum
    const originalBucket = monthBuckets.get(month.key)
    return sum + (originalBucket?.totalAge || 0)
  }, 0)
  const latestMonth = [...months].reverse().find((month) => month.sampleSize > 0) || null

  return {
    months,
    summary: {
      firstMonth: months[0] || null,
      lastMonth: months[months.length - 1] || null,
      latestMonth,
      eligibleStudentCount,
      includedStudentCount,
      missingDobCount,
      excludedOutlierCount,
      averageAge: includedStudentCount
        ? Math.round((includedAgeTotal / includedStudentCount) * 10) / 10
        : null,
      maxAge,
    },
  }
}

export const INQUIRY_AGE_MAX = DEFAULT_MAX_AGE
export const INQUIRY_AGE_MONTH_COUNT = DEFAULT_MONTH_COUNT
